import * as turf from '@turf/turf';
import * as SpatialIndex from './SpatialIndex.js';
import { Popup } from 'maplibre-gl';

const PERFORMANCE = {
  DEBOUNCE_DELAY: 400,
  MAP_UPDATE_DELAY: 100,
  DEBOUNCE_WAIT: 16,
  CACHE_TTL: 30000
};

const SPATIAL = {
  BOUNDS_THRESHOLD: 0.01,
  BOUNDS_PRECISION: 3,
  EARTH_KM_PER_DEGREE_LAT: 110.574,
  EARTH_KM_PER_DEGREE_LNG_AT_EQUATOR: 111.32
};

const CACHE_LIMITS = {
  POINT_IN_POLYGON: 10000,
  DIMENSION: 1000,
  FEATURE_BOUNDS: 1000
};

// Starting camera, and where a filter reset returns to.
export const DEFAULT_VIEW = {
  center: [-98.5795, 39.8283],
  zoom: 4
};

const caches = {
  stationGeoJSON: { data: null, key: null, timestamp: 0 },
  dimensions: new Map(),
  pointInPolygon: new Map(),
  featureBounds: new Map()
};

const CACHE_CLEANUP_INTERVAL = 5 * 60 * 1000;

 export function debounce(func, wait = PERFORMANCE.DEBOUNCE_WAIT) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function getMapBounds(map) {
  const bounds = map.getBounds();
  return {
    west: bounds.getWest(),
    south: bounds.getSouth(), 
    east: bounds.getEast(),
    north: bounds.getNorth()
  };
}

// Bounding box using the smallest longitudinal arc, so east can exceed 180.
export function getStationBounds(stations) {
  if (!Array.isArray(stations)) return null;

  const lngs = [];
  let south = Infinity;
  let north = -Infinity;

  for (const station of stations) {
    if (!Number.isFinite(station?.longitude) || !Number.isFinite(station?.latitude)) continue;
    lngs.push(station.longitude);
    if (station.latitude < south) south = station.latitude;
    if (station.latitude > north) north = station.latitude;
  }

  if (!lngs.length) return null;

  const sorted = lngs.slice().sort((a, b) => a - b);
  const count = sorted.length;

  // The widest empty sector tells us which arc to cover.
  let gapIndex = 0;                                     // wrap gap: last -> first
  let widestGap = sorted[0] + 360 - sorted[count - 1];

  for (let i = 1; i < count; i++) {
    const gap = sorted[i] - sorted[i - 1];
    if (gap > widestGap) {
      widestGap = gap;
      gapIndex = i;
    }
  }

  return {
    west: sorted[gapIndex],
    south,
    east: gapIndex === 0 ? sorted[count - 1] : sorted[gapIndex - 1] + 360,
    north
  };
}


// May need if we want to limit to zoom levels

// export function calculateOptimalLimit(zoom) {
//   if (zoom >= 12) return 500;
//   if (zoom >= 10) return 750;
//   if (zoom >= 8) return 1000;
//   if (zoom >= 6) return 1000;
//   if (zoom >= 4) return 1000;
//   return 1000;
// }

// export function shouldFetchForZoom(zoom) {
//   return zoom >= 3;
// }

function getFeatureBounds(feature) {
  const cacheKey = feature.id || JSON.stringify(feature.geometry).substring(0, 100);
  
  if (caches.featureBounds.has(cacheKey)) {
    return caches.featureBounds.get(cacheKey);
  }

  let bounds;
  if (feature.properties?.isCircle && feature.properties.center) {
    bounds = getCircleBounds(feature.properties);
  } else {
    try {
      const bbox = turf.bbox(feature);
      bounds = [bbox[0], bbox[1], bbox[2], bbox[3]];
    } catch (error) {
      console.warn('Failed to calculate feature bounds:', error);
      return null;
    }
  }

  if (caches.featureBounds.size >= CACHE_LIMITS.FEATURE_BOUNDS) {
    const firstKey = caches.featureBounds.keys().next().value;
    caches.featureBounds.delete(firstKey);
  }

  caches.featureBounds.set(cacheKey, bounds);
  return bounds;
}

function getCircleBounds(circleProps) {
  const { center, radiusKm } = circleProps;
  const radius = radiusKm;
  
  if (!center || !radius) return null;
  
  const radiusInDegrees = radius / SPATIAL.EARTH_KM_PER_DEGREE_LAT;
  const radiusInDegreesLng = radius / (SPATIAL.EARTH_KM_PER_DEGREE_LNG_AT_EQUATOR * Math.cos(center[1] * Math.PI / 180));
  
  return [
    center[0] - radiusInDegreesLng,
    center[1] - radiusInDegrees,
    center[0] + radiusInDegreesLng,
    center[1] + radiusInDegrees
  ];
}

export function pointInPolygon(point, polygon) {
  const key = `${point[0].toFixed(4)},${point[1].toFixed(4)},${polygon.id || 'noId'}`;
  
  if (caches.pointInPolygon.has(key)) {
    return caches.pointInPolygon.get(key);
  }

  let result = false;
  try {
    if (polygon.properties?.isCircle && polygon.properties.center) {
      const { center, radiusKm } = polygon.properties;
      const radius = radiusKm;
      const distance = turf.distance(turf.point(center), turf.point(point), { units: 'kilometers' });
      result = distance <= radius;
    } else {
      result = turf.booleanPointInPolygon(turf.point(point), polygon.geometry);
    }
  } catch (error) {
    console.warn('Point in polygon calculation failed:', error);
    result = false;
  }

  if (caches.pointInPolygon.size >= CACHE_LIMITS.POINT_IN_POLYGON) {
    const firstKey = caches.pointInPolygon.keys().next().value;
    caches.pointInPolygon.delete(firstKey);
  }
  
  caches.pointInPolygon.set(key, result);
  return result;
}

export function calculateFeatureDimensions(feature) {
  const cacheKey = feature.id || JSON.stringify(feature.geometry).substring(0, 100);
  
  if (caches.dimensions.has(cacheKey)) {
    return caches.dimensions.get(cacheKey);
  }

  let result = null;

  try {
    if (feature.properties?.isCircle && feature.properties.center) {
      const { center, radiusKm } = feature.properties;
      const radius = radiusKm;

      if (center && radius) {
        const area = Math.PI * radius * radius;
        const offsetLng = (radius / SPATIAL.EARTH_KM_PER_DEGREE_LNG_AT_EQUATOR) * 1.2;
        
        result = {
          dimensions: {
            type: 'circle',
            radius: radius.toFixed(1),
            area: area.toFixed(1)
          },
          position: [center[0] + offsetLng, center[1]]
        };
      }
    } else if (feature.geometry?.type === 'Polygon' && feature.geometry.coordinates?.[0]?.length >= 4) {
      const area = turf.area(feature) / 1000000;
      const bbox = turf.bbox(feature);
      
      result = {
        dimensions: {
          type: 'polygon',
          area: area.toFixed(1)
        },
        position: [bbox[2], (bbox[1] + bbox[3]) / 2]
      };
    }
  } catch (error) {
    console.warn('Feature dimension calculation failed:', error);
    return null;
  }

  if (caches.dimensions.size >= CACHE_LIMITS.DIMENSION) {
    const firstKey = caches.dimensions.keys().next().value;
    caches.dimensions.delete(firstKey);
  }

  caches.dimensions.set(cacheKey, result);
  return result;
}

export function createDimensionPopup(feature, map) {
  try {
    const result = calculateFeatureDimensions(feature);
    if (!result || !map) return null;

    const { dimensions, position } = result;
    
    const content = dimensions.type === 'circle'
      ? `<div class="dimension-display">
          <strong>Radius:</strong> ${dimensions.radius} km<br>
          <strong>Area:</strong> ${dimensions.area} km²
        </div>`
      : `<div class="dimension-display">
          <strong>Area:</strong> ${dimensions.area} km²
        </div>`;

    return new Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'dimension-popup',
      anchor: 'left',
      offset: [10, 0]
    })
      .setLngLat(position)
      .setHTML(content)
      .addTo(map);
  } catch (error) {
    console.warn('Dimension popup creation failed:', error);
    return null;
  }
}

export function stationsToGeoJSON(stations) {
  const cacheKey = stations.map(s => `${s.id}:${s.latitude}:${s.longitude}`).join('|');
  const now = Date.now();
  
  if (caches.stationGeoJSON.key === cacheKey && 
      caches.stationGeoJSON.data &&
      (now - caches.stationGeoJSON.timestamp) < PERFORMANCE.CACHE_TTL) {
    return caches.stationGeoJSON.data;
  }

  const geoJSON = {
    type: 'FeatureCollection',
    features: stations.map(station => ({
      type: 'Feature',
      id: station.id,
      geometry: {
        type: 'Point',
        coordinates: [station.longitude, station.latitude]
      },
      properties: {
        id: station.id,
        name: station.name,
        beginDate: station.beginDate,
        endDate: station.endDate,
        prefix: station.prefix
      }
    }))
  };

  caches.stationGeoJSON.data = geoJSON;
  caches.stationGeoJSON.key = cacheKey;
  caches.stationGeoJSON.timestamp = now;
  
  return geoJSON;
}

export function findStationsInFeature(stations, feature) {
  try {
    if (!stations.length || !feature) return [];

    if (SpatialIndex.isInitialized()) {
      const results = SpatialIndex.findStationsInFeature(feature);
      return results.map(s => s.id);
    }

    const bbox = getFeatureBounds(feature);
    if (!bbox) return [];

    const candidates = stations.filter(station => 
      station.longitude >= bbox[0] && 
      station.longitude <= bbox[2] &&
      station.latitude >= bbox[1] && 
      station.latitude <= bbox[3]
    );

    return candidates
      .filter(station => pointInPolygon([station.longitude, station.latitude], feature))
      .map(station => station.id);
  } catch (error) {
    console.warn('Find stations in feature failed:', error);
    return [];
  }
}

export const updateSelectionFilters = debounce((map, selectedStations) => {
  try {
    if (!map || !map.getLayer('selected-stations')) {
      return false;
    }

    const stationIds = Array.isArray(selectedStations) ? selectedStations : [];
    const filter = ['in', ['get', 'id'], ['literal', stationIds]];
    
    map.setFilter('selected-stations', filter);
    map.setFilter('selected-station-labels', filter);
    
    return true;
  } catch (error) {
    console.warn('Update selection filters failed:', error);
    return false;
  }
}, PERFORMANCE.DEBOUNCE_WAIT);

function cleanupCaches() {
  const now = Date.now();
  
  if (caches.stationGeoJSON.timestamp && 
      (now - caches.stationGeoJSON.timestamp) > 5 * 60 * 1000) {
    caches.stationGeoJSON.data = null;
    caches.stationGeoJSON.key = null;
    caches.stationGeoJSON.timestamp = 0;
  }
  
  if (caches.pointInPolygon.size > CACHE_LIMITS.POINT_IN_POLYGON * 1.5) {
    const keysToDelete = Array.from(caches.pointInPolygon.keys()).slice(0, CACHE_LIMITS.POINT_IN_POLYGON / 2);
    keysToDelete.forEach(key => caches.pointInPolygon.delete(key));
  }

  if (caches.dimensions.size > CACHE_LIMITS.DIMENSION * 1.5) {
    const keysToDelete = Array.from(caches.dimensions.keys()).slice(0, CACHE_LIMITS.DIMENSION / 2);
    keysToDelete.forEach(key => caches.dimensions.delete(key));
  }

  if (caches.featureBounds.size > CACHE_LIMITS.FEATURE_BOUNDS * 1.5) {
    const keysToDelete = Array.from(caches.featureBounds.keys()).slice(0, CACHE_LIMITS.FEATURE_BOUNDS / 2);
    keysToDelete.forEach(key => caches.featureBounds.delete(key));
  }
}

let cacheCleanupInterval = null;

export function startCacheCleanup() {
  if (cacheCleanupInterval) return;
  cacheCleanupInterval = setInterval(cleanupCaches, CACHE_CLEANUP_INTERVAL);
}

export function stopCacheCleanup() {
  if (cacheCleanupInterval) {
    clearInterval(cacheCleanupInterval);
    cacheCleanupInterval = null;
  }
}

export function clearCaches() {
  caches.stationGeoJSON.data = null;
  caches.stationGeoJSON.key = null;
  caches.stationGeoJSON.timestamp = 0;
  caches.dimensions.clear();
  caches.pointInPolygon.clear();
  caches.featureBounds.clear();
}

// May need if we want to expand the fetching bounds outside of viewport as a buffer

// export function expandBounds(bounds, bufferPercentage = 0.5) {
//   if (!bounds) return null;

//   const width = bounds.east - bounds.west;
//   const height = bounds.north - bounds.south;

//   const widthBuffer = (width * bufferPercentage) / 2;
//   const heightBuffer = (height * bufferPercentage) / 2;

//   const newWest = bounds.west - widthBuffer;
//   const newSouth = bounds.south - heightBuffer;
//   const newEast = bounds.east + widthBuffer;
//   const newNorth = bounds.north + heightBuffer;

//   return {
//     west: Math.max(-180, newWest),
//     south: Math.max(-90, newSouth),
//     east: Math.min(180, newEast),
//     north: Math.min(90, newNorth)
//   };
// }